import React, { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { QrCode, Send, ChefHat, CreditCard, Users, Smartphone, ArrowRight, ChevronLeft, ChevronRight, Bell, UserCheck, Sparkles } from 'lucide-react'
import { ImageGallery } from '../../common/ImageGallery'

export const BaresOrderingFlow: React.FC = () => {
  const { t } = useTranslation('bares')
  const gallery = [
    { src: '/images/bares/03-qr-identificacao-mobile.png', alt: 'QR - Identificação', title: 'Entrada por QR', description: 'Cliente informa o nome e entra na mesa.' },
    { src: '/images/bares/04-menu-lista-mobile.png', alt: 'Menu - Lista', title: 'Cardápio no celular', description: 'Categorias, busca e destaques.' },
    { src: '/images/bares/05-item-detalhe-mobile.png', alt: 'Item - Detalhe', title: 'Detalhe do item', description: 'Descrição e adicionar ao pedido.' },
    { src: '/images/bares/06-meus-pedidos-mobile.png', alt: 'Meus Pedidos', title: 'Meus pedidos', description: 'Acompanhe status dos itens.' },
    { src: '/images/bares/07-minha-conta-pessoa-mobile.png', alt: 'Minha Conta', title: 'Conta do convidado', description: 'Cada um vê e paga o seu.' },
    { src: '/images/bares/08-conta-mesa-mobile.png', alt: 'Conta da Mesa', title: 'Conta da mesa', description: 'Visão consolidada e divisão por pessoa.' },
    { src: '/images/bares/09-pagamento-pix-mobile.png', alt: 'Pagamento PIX', title: 'Pagamento', description: 'PIX direto pelo celular.' }
  ]
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const openImage = (idx: number) => {
    setIndex(idx)
    setOpen(true)
  }

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10)
    }
  }

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
      setTimeout(checkScroll, 300)
    }
  }

  useEffect(() => {
    checkScroll()
    const handleResize = () => checkScroll()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const steps = [
    { icon: QrCode, title: t('flow.steps.scan.title'), desc: t('flow.steps.scan.desc'), color: 'text-amber-600', bg: 'bg-amber-50', number: '01' },
    { icon: Send, title: t('flow.steps.order.title'), desc: t('flow.steps.order.desc'), color: 'text-orange-600', bg: 'bg-orange-50', number: '02' },
    { icon: ChefHat, title: t('flow.steps.kds.title'), desc: t('flow.steps.kds.desc'), color: 'text-yellow-600', bg: 'bg-yellow-50', number: '03' },
    { icon: CreditCard, title: t('flow.steps.pay.title'), desc: t('flow.steps.pay.desc'), color: 'text-green-600', bg: 'bg-green-50', number: '04' }
  ]

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 via-amber-50/30 to-orange-50/30 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-300/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-300/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4"
          >
            <Smartphone className="w-4 h-4" />
            {t('flow.kicker', 'Do pedido ao pagamento')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            {t('flow.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('flow.subtitle')}
          </motion.p>
        </div>

        {/* Process Steps with Connection Lines */}
        <div className="relative mb-16">
          <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-200 via-orange-200 to-green-200"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="relative"
              >
                <div className={`bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-14 h-14 ${s.bg} rounded-xl flex items-center justify-center shadow-md`}>
                      <s.icon className={`w-7 h-7 ${s.color}`} />
                    </div>
                    <span className={`text-3xl font-bold ${s.color} opacity-20`}>{s.number}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 -right-3 z-10">
                    <ArrowRight className={`w-6 h-6 ${s.color}`} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Flexibility Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-3">{t('flow.flexibility.title')}</h3>
            <p className="text-lg text-gray-600">{t('flow.flexibility.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 shadow-lg border-2 border-amber-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Bell className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900">{t('flow.flexibility.callWaiter.title')}</h4>
              </div>
              <p className="text-gray-700">{t('flow.flexibility.callWaiter.desc')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 shadow-lg border-2 border-orange-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-orange-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900">{t('flow.flexibility.hybrid.title')}</h4>
              </div>
              <p className="text-gray-700">{t('flow.flexibility.hybrid.desc')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-6 shadow-lg border-2 border-yellow-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-yellow-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900">{t('flow.flexibility.choice.title')}</h4>
              </div>
              <p className="text-gray-700">{t('flow.flexibility.choice.desc')}</p>
            </motion.div>
          </div>
        </motion.div>

        {/* Benefits Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 shadow-lg border border-amber-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Para o cliente</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                <span className="text-gray-700">O cliente pede pelo QR e acompanha o pedido.</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                <span className="text-gray-700">Vê a própria conta separada, sem confusão.</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                <span className="text-gray-700">Paga o próprio consumo quando quiser.</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-8 shadow-lg border border-orange-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Para a equipe</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                <span className="text-gray-700">Pedidos chegam organizados por estação (bar/cozinha).</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                <span className="text-gray-700">Status simples: preparando, pronto, entregue.</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                <span className="text-gray-700">Quitar por pessoa ou por mesa em poucos cliques.</span>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Mobile Screenshots Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Veja a experiência mobile completa</h3>
          <p className="text-gray-600">Toque nas telas para ampliar e explorar cada etapa</p>
        </motion.div>

        <div className="relative">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-amber-50 transition-colors"
              aria-label="Ver imagens anteriores"
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
          )}

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-amber-50 transition-colors"
              aria-label="Ver próximas imagens"
            >
              <ChevronRight className="w-6 h-6 text-gray-700" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            onScroll={checkScroll}
            className="overflow-x-auto pb-4 scrollbar-hide"
          >
            <div className="flex gap-4 min-w-max px-4">
              {gallery.map((img, i) => (
                <motion.button
                  key={img.src}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="group bg-white rounded-2xl border-2 border-gray-200 p-4 shadow-md hover:shadow-2xl hover:border-amber-300 transition-all duration-300 hover:-translate-y-2"
                  onClick={() => openImage(i)}
                >
                  <img src={img.src} alt={img.alt} className="h-80 w-auto object-contain" />
                  <div className="mt-3 text-sm font-semibold text-gray-700 group-hover:text-amber-600 transition-colors">{img.title}</div>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
        <ImageGallery images={gallery} isOpen={open} onClose={() => setOpen(false)} initialIndex={index} />
      </div>
    </section>
  )
}
